import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import { PageContext } from '@src/config';
import { getPageListData } from '@src/config/getPageListData';
import type { IPageContextConfig } from '@src/config/PageContext';
import { MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import ProductList from '@src/containers/list/ProductList';
import { useBestProducts } from '@src/hooks/useBestProducts';
import { useListPage } from '@src/hooks/useListPage';
import { useLoadRestOffers } from '@src/hooks/useLoadRestOffers';
import { Layout } from 'app/Layout';
import { fullDealOfferAction, sravniOfferAction } from 'entities/Microcredits/lib/sravniOfferAction';
import { CardActionProvider } from 'entities/Microcredits/ui/CardActionProvider';
import { AnchorLinkList } from 'shared/ui';
import { LightweightCard } from 'widgets/LightweightCard';
import { MobileSubHeader } from 'widgets/MobileSubHeader';
import { SeoComponents } from 'widgets/SeoComponents';

import { CARD_CONTENT } from './CalculatorPage.config';
import styles from './CalculatorPage.module.scss';
import { CalculatorPageHeader } from './CalculatorPageHeader';

const pageConfig: IPageContextConfig = {
  listKey: ListKeys.CALCULATOR,
  source: 'search',
  cardTermIsVisible: true,
  withCardProductLink: true,
  sortPanelVisible: true,
};

export const CalculatorPage: NextPage = () => {
  const isMobile = useIsMobile();
  const { filter, seo, pathname, hasUtmLabel } = useListPage(ListKeys.CALCULATOR);
  const config = useMemo(() => ({ ...pageConfig, withMonetizationCardLogoClick: isMobile }), [isMobile]);

  const hasSeoBlocks = !hasUtmLabel && Boolean(seo);

  useLoadRestOffers(ListKeys.CALCULATOR);
  useBestProducts();

  return (
    <Layout>
      <PageContext.Provider value={config}>
        <CalculatorPageHeader filters={filter.filters} />

        {!hasUtmLabel && <AnchorLinkList className={styles.anchorLinkList} />}

        <MobileSubHeader />
        <CardActionProvider rules={[sravniOfferAction, fullDealOfferAction]}>
          <ProductList
            gridLayout
            filter={filter}
            pathname={pathname}
            renderCard={(props) => (
              <LightweightCard hasUtmLabel={hasUtmLabel} {...props} contentSettings={CARD_CONTENT} />
            )}
          />
        </CardActionProvider>
        {hasSeoBlocks && (
          <SeoComponents
            seo={seo}
            breadcrumbs={seo.breadcrumbs}
            prefooter={seo.prefooter}
            showAnchorLinks={false}
            showSubheader={false}
          />
        )}
      </PageContext.Provider>
    </Layout>
  );
};

CalculatorPage.getInitialProps = (ctx: Application.ReduxNextPageContext) =>
  getPageListData(ctx, {
    key: ListKeys.CALCULATOR,
    defaultFiltersMode: MODES.CALCULATOR,
    allowFullDealOffersExperiment: false,
  });
